import { upsertUserMetadata } from 'actions';
import MultiselectDropdownRedesign, {
  MultiSelectOption,
} from 'components/MultiSelectDropdown/MultiSelectDropdownRedesign';
import ToggleButtonRedesign from 'components/ToggleButton/ToggleButtonRedesign';
import Labels from 'components/UsersPermissions/UserPermissionsForm/Labels';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getCustomer, getUser } from 'selectors/auth';
import { getCurrentUserMetadata, getUserPreferOriginalLanguages } from 'selectors/users';
import languagesMapping from 'utils/languages';
import {
  getUserMetadataData,
  PREFER_ORIGINAL_ALL_LANGUAGES,
  UserMetadataData,
} from 'utils/userMetadata';

const ALL_LANGUAGES_OPTION: MultiSelectOption = {
  value: PREFER_ORIGINAL_ALL_LANGUAGES,
  label: 'All languages',
};

const UserHighlightSettings: React.FC = () => {
  const dispatch = useDispatch();
  const user = useSelector(getUser);
  const customer = useSelector(getCustomer);
  const userMetadata = useSelector(getCurrentUserMetadata);
  const preferOriginalLanguages = useSelector(getUserPreferOriginalLanguages);
  const metadataData = getUserMetadataData(userMetadata);
  const showHighlights = Boolean(metadataData.showHighlights);
  const preferOriginalLanguagesKey = preferOriginalLanguages.join(',');

  const [isOn, setIsOn] = useState(showHighlights);
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>(preferOriginalLanguages);

  useEffect(() => {
    setIsOn(showHighlights);
    setSelectedLanguages(preferOriginalLanguages);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showHighlights, preferOriginalLanguagesKey]);

  const languageOptions = useMemo(() => {
    // @ts-ignore
    const allowedTranslations: string[] | undefined = customer?.config?.allowed_translations;

    const languages = languagesMapping
      .filter((language) => language.value !== 'en')
      .filter(
        (language) =>
          !allowedTranslations ||
          allowedTranslations.includes('*') ||
          allowedTranslations.includes(language.value)
      )
      .map((language) => ({ value: language.value, label: language.label }));

    return [ALL_LANGUAGES_OPTION, ...languages];
  }, [customer]);

  const selectedLanguageItems = useMemo(
    () =>
      selectedLanguages
        .map((code) => languageOptions.find((option) => option.value === code))
        .filter((option): option is MultiSelectOption => Boolean(option)),
    [selectedLanguages, languageOptions]
  );

  const persistMetadata = (data: UserMetadataData): void => {
    dispatch(
      upsertUserMetadata({
        uuid: userMetadata?.uuid,
        entity_type: userMetadata?.entity_type ?? 'User',
        entity_uuid: userMetadata?.entity_uuid ?? user.uuid,
        data,
      })
    );
  };

  const handleHighlightToggle = (): void => {
    const nextShowHighlights = !isOn;
    setIsOn(nextShowHighlights);
    persistMetadata({
      showHighlights: nextShowHighlights,
      preferOriginalLanguages: selectedLanguages,
    });
  };

  const persistLanguages = (nextLanguages: string[]): void => {
    setSelectedLanguages(nextLanguages);
    persistMetadata({
      showHighlights: isOn,
      preferOriginalLanguages: nextLanguages,
    });
  };

  const handleLanguageChange = (option: MultiSelectOption): void => {
    const isSelected = selectedLanguages.includes(option.value);

    if (option.value === PREFER_ORIGINAL_ALL_LANGUAGES) {
      persistLanguages(isSelected ? [] : [PREFER_ORIGINAL_ALL_LANGUAGES]);
      return;
    }

    if (isSelected) {
      persistLanguages(selectedLanguages.filter((code) => code !== option.value));
      return;
    }

    const withoutAll = selectedLanguages.filter((code) => code !== PREFER_ORIGINAL_ALL_LANGUAGES);
    persistLanguages([...withoutAll, option.value]);
  };

  const handleLanguageDelete = (option: MultiSelectOption): void => {
    persistLanguages(selectedLanguages.filter((code) => code !== option.value));
  };

  return (
    <div className="flex flex-col w-1/2  px-4 border-b border-litlingo-gray-2">
      <span className="font-header font-bold text-xl">User Settings</span>
      <div className=" flex flex-row justify-between mt-4">
        <div>
          <span className="">Reviewer View </span>
          <span className="italic text-litlingo-gray-100">(Ctrl + R) to toggle</span>
        </div>
        <ToggleButtonRedesign
          dataTestid="show-highlight-button"
          isOn={isOn}
          handleToggleClick={(): void => {
            handleHighlightToggle();
          }}
        />
      </div>
      <span className="text-small text-litlingo-gray-100 mb-4">
        This view includes passage highlights
      </span>

      <div className="flex flex-col gap-1 mt-2">
        <span className="">Prefer original for languages</span>
        <span className="text-small text-litlingo-gray-100">
          Communications in these languages will default to the original. You can still switch to
          the translation.
        </span>
      </div>
      <div className="flex flex-row gap-2 mt-3 mb-4">
        <div className="min-w-42 w-56">
          <MultiselectDropdownRedesign
            placeholder="Select languages"
            onChange={handleLanguageChange}
            options={languageOptions}
            selectedItems={selectedLanguageItems}
            dataTestid="prefer-original-languages"
            enableSearch
            searchPlaceholder="Search languages"
          />
        </div>
        <Labels includedLabels={selectedLanguageItems} handleDelete={handleLanguageDelete} />
      </div>
    </div>
  );
};

export default UserHighlightSettings;
